import { ComponentBase } from '@abstracts/component-base';
import { Component, Injector, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UpdateSubscriptionTypePermissions } from '@dto/updateSubscriptionTypePermissions';
import { CustomerPermission } from '@models/customerPermission';
import { SubscriptionTypePermission } from '@models/subscriptionTypePermission';
import { CustomersService } from '@services/entity/customers.service';
import { OperatorsService } from '@services/entity/operators.service';
import { SubscriptionsService } from '@services/entity/subscriptions.service';
import { FormService } from '@services/form/form.service';
import { ToastService } from '@services/toast/toast.service';
import { plainToClass } from 'class-transformer';

@Component({
  selector: 'app-subscription-types',
  templateUrl: './subscription-types.component.html',
  styleUrls: ['./subscription-types.component.scss']
})
export class SubscriptionTypesComponent extends ComponentBase implements OnInit {

  public className = 'SubscriptionTypesComponent';

  public basicPermissionMap: Map<string, SubscriptionTypePermission>;
  public standardPermissionMap: Map<string, SubscriptionTypePermission>;
  public premiumPermissionMap: Map<string, SubscriptionTypePermission>;

  public formCreated: boolean;

  private basicReady: boolean;
  private standardReady: boolean;
  private premiumReady: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private subscriptionsService: SubscriptionsService ,
    public formService: FormService,
    public toastService: ToastService,
    injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadPermissions();
  }

  loadPermissions() {
    this.basicReady = false;
    this.standardReady = false;
    this.premiumReady = false;

    this.subscriptionsService.getSubscriptionTypePermissions(1).subscribe(result => {
      this.setupBasicPermissionMap(result);
    }, error => { });

    this.subscriptionsService.getSubscriptionTypePermissions(2).subscribe(result => {
      this.setupStandardPermissionMap(result);
    }, error => { });

    this.subscriptionsService.getSubscriptionTypePermissions(3).subscribe(result => {
      this.setupPremiumPermissionMap(result);
    }, error => { });
  }

  checkIfAllPermissionsReady() {
    if (this.basicReady && this.standardReady && this.premiumReady) {
      this.createForm();
    }
  }

  setupBasicPermissionMap(subscriptionTypePermissions: SubscriptionTypePermission[]) {
    this.basicPermissionMap = new Map<string, SubscriptionTypePermission>();

    subscriptionTypePermissions.forEach(subscriptionTypePermission => {
      this.basicPermissionMap.set(subscriptionTypePermission.permission.name, subscriptionTypePermission);
    })

    this.basicReady = true;
    this.checkIfAllPermissionsReady();
  }

  setupStandardPermissionMap(subscriptionTypePermissions: SubscriptionTypePermission[]) {
    this.standardPermissionMap = new Map<string, SubscriptionTypePermission>();

    subscriptionTypePermissions.forEach(subscriptionTypePermission => {
      this.standardPermissionMap.set(subscriptionTypePermission.permission.name, subscriptionTypePermission);
    })

    this.standardReady = true;
    this.checkIfAllPermissionsReady();
  }

  setupPremiumPermissionMap(subscriptionTypePermissions: SubscriptionTypePermission[]) {
    this.premiumPermissionMap = new Map<string, SubscriptionTypePermission>();

    subscriptionTypePermissions.forEach(subscriptionTypePermission => {
      this.premiumPermissionMap.set(subscriptionTypePermission.permission.name, subscriptionTypePermission);
    })

    this.premiumReady = true;
    this.checkIfAllPermissionsReady();
  }

  createForm() {
    var basicGroup = new UntypedFormGroup({
      navDashboard: new UntypedFormControl(this.basicPermissionMap.get("navDashboard").value),
      navChargingstations: new UntypedFormControl(this.basicPermissionMap.get("navChargingstations").value),
      navEvaluation: new UntypedFormControl(this.basicPermissionMap.get("navEvaluation").value),
      navManage: new UntypedFormControl(this.basicPermissionMap.get("navManage").value),
      dashboardChargingstations: new UntypedFormControl(this.basicPermissionMap.get("dashboardChargingstations").value),
      dashboardPowerchart: new UntypedFormControl(this.basicPermissionMap.get("dashboardPowerchart").value),
      dashboardLatestTransactions: new UntypedFormControl(this.basicPermissionMap.get("dashboardLatestTransactions").value),
      dashboardProfile: new UntypedFormControl(this.basicPermissionMap.get("dashboardProfile").value),
      dashboardCo2: new UntypedFormControl(this.standardPermissionMap.get("dashboardCo2").value),
      dashboardPowerchartDay: new UntypedFormControl(this.basicPermissionMap.get("dashboardPowerchartDay").value),
      dashboardPowerchartMonth: new UntypedFormControl(this.basicPermissionMap.get("dashboardPowerchartMonth").value),
      dashboardPowerchartYear: new UntypedFormControl(this.basicPermissionMap.get("dashboardPowerchartYear").value),
      chargingstationsGroups: new UntypedFormControl(this.basicPermissionMap.get("chargingstationsGroups").value),
      chargingstationsCreate: new UntypedFormControl(this.basicPermissionMap.get("chargingstationsCreate").value),
      chargingstationDetailsAuth: new UntypedFormControl(this.basicPermissionMap.get("chargingstationDetailsAuth").value),
      chargingstationDetailsTransactions: new UntypedFormControl(this.basicPermissionMap.get("chargingstationDetailsTransactions").value),
      chargingstationFreecharge: new UntypedFormControl(this.basicPermissionMap.get("chargingstationFreecharge").value),
      chargingstationRemoteStart: new UntypedFormControl(this.basicPermissionMap.get("chargingstationRemoteStart").value),
      chargingstationGroupsCreate: new UntypedFormControl(this.basicPermissionMap.get("chargingstationGroupsCreate").value),
      evaluationTransactions: new UntypedFormControl(this.basicPermissionMap.get("evaluationTransactions").value),
      evaluationDeliveryNotes: new UntypedFormControl(this.basicPermissionMap.get("evaluationDeliveryNotes").value),
      createDeliveryNotes: new UntypedFormControl(this.basicPermissionMap.get("createDeliveryNotes").value),
      chargingStatisticsWeek: new UntypedFormControl(this.basicPermissionMap.get("chargingStatisticsWeek").value),
      chargingStatisticsMonth: new UntypedFormControl(this.basicPermissionMap.get("chargingStatisticsMonth").value),
      chargingStatisticsYear: new UntypedFormControl(this.basicPermissionMap.get("chargingStatisticsYear").value),
      chargingStatisticsCustom: new UntypedFormControl(this.basicPermissionMap.get("chargingStatisticsCustom").value),
      chargingStatisticsFilter: new UntypedFormControl(this.basicPermissionMap.get("chargingStatisticsFilter").value),
      chargingStatisticsCheckbox: new UntypedFormControl(this.basicPermissionMap.get("chargingStatisticsCheckbox").value),
      chargingStatisticsDateFilter: new UntypedFormControl(this.basicPermissionMap.get("chargingStatisticsDateFilter").value),
      evaluationDeliveryNotesSend: new UntypedFormControl(this.basicPermissionMap.get("evaluationDeliveryNotesSend").value),
      manageUsersCreate: new UntypedFormControl(this.basicPermissionMap.get("manageUsersCreate").value),
      manageUsersCreateCount: new UntypedFormControl(this.basicPermissionMap.get("manageUsersCreateCount").value),
      manageRfidScan: new UntypedFormControl(this.basicPermissionMap.get("manageRfidScan").value)
    });

    var standardGroup = new UntypedFormGroup({
      navDashboard: new UntypedFormControl(this.standardPermissionMap.get("navDashboard").value),
      navChargingstations: new UntypedFormControl(this.standardPermissionMap.get("navChargingstations").value),
      navEvaluation: new UntypedFormControl(this.standardPermissionMap.get("navEvaluation").value),
      navManage: new UntypedFormControl(this.standardPermissionMap.get("navManage").value),
      dashboardChargingstations: new UntypedFormControl(this.standardPermissionMap.get("dashboardChargingstations").value),
      dashboardPowerchart: new UntypedFormControl(this.standardPermissionMap.get("dashboardPowerchart").value),
      dashboardLatestTransactions: new UntypedFormControl(this.standardPermissionMap.get("dashboardLatestTransactions").value),
      dashboardProfile: new UntypedFormControl(this.standardPermissionMap.get("dashboardProfile").value),
      dashboardCo2: new UntypedFormControl(this.standardPermissionMap.get("dashboardCo2").value),
      dashboardPowerchartDay: new UntypedFormControl(this.standardPermissionMap.get("dashboardPowerchartDay").value),
      dashboardPowerchartMonth: new UntypedFormControl(this.standardPermissionMap.get("dashboardPowerchartMonth").value),
      dashboardPowerchartYear: new UntypedFormControl(this.standardPermissionMap.get("dashboardPowerchartYear").value),
      chargingstationsGroups: new UntypedFormControl(this.standardPermissionMap.get("chargingstationsGroups").value),
      chargingstationsCreate: new UntypedFormControl(this.standardPermissionMap.get("chargingstationsCreate").value),
      chargingstationDetailsAuth: new UntypedFormControl(this.standardPermissionMap.get("chargingstationDetailsAuth").value),
      chargingstationDetailsTransactions: new UntypedFormControl(this.standardPermissionMap.get("chargingstationDetailsTransactions").value),
      chargingstationFreecharge: new UntypedFormControl(this.standardPermissionMap.get("chargingstationFreecharge").value),
      chargingstationRemoteStart: new UntypedFormControl(this.standardPermissionMap.get("chargingstationRemoteStart").value),
      chargingstationGroupsCreate: new UntypedFormControl(this.standardPermissionMap.get("chargingstationGroupsCreate").value),
      evaluationTransactions: new UntypedFormControl(this.standardPermissionMap.get("evaluationTransactions").value),
      evaluationDeliveryNotes: new UntypedFormControl(this.standardPermissionMap.get("evaluationDeliveryNotes").value),
      createDeliveryNotes: new UntypedFormControl(this.standardPermissionMap.get("createDeliveryNotes").value),
      chargingStatisticsWeek: new UntypedFormControl(this.standardPermissionMap.get("chargingStatisticsWeek").value),
      chargingStatisticsMonth: new UntypedFormControl(this.standardPermissionMap.get("chargingStatisticsMonth").value),
      chargingStatisticsYear: new UntypedFormControl(this.standardPermissionMap.get("chargingStatisticsYear").value),
      chargingStatisticsCustom: new UntypedFormControl(this.standardPermissionMap.get("chargingStatisticsCustom").value),
      chargingStatisticsFilter: new UntypedFormControl(this.standardPermissionMap.get("chargingStatisticsFilter").value),
      chargingStatisticsCheckbox: new UntypedFormControl(this.standardPermissionMap.get("chargingStatisticsCheckbox").value),
      chargingStatisticsDateFilter: new UntypedFormControl(this.standardPermissionMap.get("chargingStatisticsDateFilter").value),
      evaluationDeliveryNotesSend: new UntypedFormControl(this.standardPermissionMap.get("evaluationDeliveryNotesSend").value),
      manageUsersCreate: new UntypedFormControl(this.standardPermissionMap.get("manageUsersCreate").value),
      manageUsersCreateCount: new UntypedFormControl(this.standardPermissionMap.get("manageUsersCreateCount").value),
      manageRfidScan: new UntypedFormControl(this.standardPermissionMap.get("manageRfidScan").value)
    });

    var premiumGroup = new UntypedFormGroup({
      navDashboard: new UntypedFormControl(this.premiumPermissionMap.get("navDashboard").value),
      navChargingstations: new UntypedFormControl(this.premiumPermissionMap.get("navChargingstations").value),
      navEvaluation: new UntypedFormControl(this.premiumPermissionMap.get("navEvaluation").value),
      navManage: new UntypedFormControl(this.premiumPermissionMap.get("navManage").value),
      dashboardChargingstations: new UntypedFormControl(this.premiumPermissionMap.get("dashboardChargingstations").value),
      dashboardPowerchart: new UntypedFormControl(this.premiumPermissionMap.get("dashboardPowerchart").value),
      dashboardLatestTransactions: new UntypedFormControl(this.premiumPermissionMap.get("dashboardLatestTransactions").value),
      dashboardProfile: new UntypedFormControl(this.premiumPermissionMap.get("dashboardProfile").value),
      dashboardCo2: new UntypedFormControl(this.standardPermissionMap.get("dashboardCo2").value),
      dashboardPowerchartDay: new UntypedFormControl(this.premiumPermissionMap.get("dashboardPowerchartDay").value),
      dashboardPowerchartMonth: new UntypedFormControl(this.premiumPermissionMap.get("dashboardPowerchartMonth").value),
      dashboardPowerchartYear: new UntypedFormControl(this.premiumPermissionMap.get("dashboardPowerchartYear").value),
      chargingstationsGroups: new UntypedFormControl(this.premiumPermissionMap.get("chargingstationsGroups").value),
      chargingstationsCreate: new UntypedFormControl(this.premiumPermissionMap.get("chargingstationsCreate").value),
      chargingstationDetailsAuth: new UntypedFormControl(this.premiumPermissionMap.get("chargingstationDetailsAuth").value),
      chargingstationDetailsTransactions: new UntypedFormControl(this.premiumPermissionMap.get("chargingstationDetailsTransactions").value),
      chargingstationFreecharge: new UntypedFormControl(this.premiumPermissionMap.get("chargingstationFreecharge").value),
      chargingstationRemoteStart: new UntypedFormControl(this.premiumPermissionMap.get("chargingstationRemoteStart").value),
      chargingstationGroupsCreate: new UntypedFormControl(this.premiumPermissionMap.get("chargingstationGroupsCreate").value),
      evaluationTransactions: new UntypedFormControl(this.premiumPermissionMap.get("evaluationTransactions").value),
      evaluationDeliveryNotes: new UntypedFormControl(this.premiumPermissionMap.get("evaluationDeliveryNotes").value),
      createDeliveryNotes: new UntypedFormControl(this.premiumPermissionMap.get("createDeliveryNotes").value),
      chargingStatisticsWeek: new UntypedFormControl(this.premiumPermissionMap.get("chargingStatisticsWeek").value),
      chargingStatisticsMonth: new UntypedFormControl(this.premiumPermissionMap.get("chargingStatisticsMonth").value),
      chargingStatisticsYear: new UntypedFormControl(this.premiumPermissionMap.get("chargingStatisticsYear").value),
      chargingStatisticsCustom: new UntypedFormControl(this.premiumPermissionMap.get("chargingStatisticsCustom").value),
      chargingStatisticsFilter: new UntypedFormControl(this.premiumPermissionMap.get("chargingStatisticsFilter").value),
      chargingStatisticsCheckbox: new UntypedFormControl(this.premiumPermissionMap.get("chargingStatisticsCheckbox").value),
      chargingStatisticsDateFilter: new UntypedFormControl(this.premiumPermissionMap.get("chargingStatisticsDateFilter").value),
      evaluationDeliveryNotesSend: new UntypedFormControl(this.premiumPermissionMap.get("evaluationDeliveryNotesSend").value),
      manageUsersCreate: new UntypedFormControl(this.premiumPermissionMap.get("manageUsersCreate").value),
      manageUsersCreateCount: new UntypedFormControl(this.premiumPermissionMap.get("manageUsersCreateCount").value),
      manageRfidScan: new UntypedFormControl(this.premiumPermissionMap.get("manageRfidScan").value)
    });

    this.formService.form = new UntypedFormGroup({
      basic: basicGroup,
      standard: standardGroup,
      premium: premiumGroup
    });

    this.formService.formMap = new Map<string, UntypedFormGroup>();
    this.formService.formMap.set("basic", basicGroup);
    this.formService.formMap.set("standard", standardGroup);
    this.formService.formMap.set("premium", premiumGroup);
    this.formCreated = true;
  }

  onSubmit(formValue) {
    for (var [key, value] of Object.entries(formValue.basic)) {
      var numberValue;

      if (typeof value == "boolean") {
        numberValue = Number(value);
      }
      else {
        numberValue = value;
      }

      var subscriptionTypePermission = this.basicPermissionMap.get(key);
      subscriptionTypePermission.value = numberValue;

      this.basicPermissionMap.set(key, subscriptionTypePermission);
    }

    for (var [key, value] of Object.entries(formValue.standard)) {
      var numberValue;

      if (typeof value == "boolean") {
        numberValue = Number(value);
      }
      else {
        numberValue = value;
      }

      var subscriptionTypePermission = this.standardPermissionMap.get(key);
      subscriptionTypePermission.value = numberValue;

      this.standardPermissionMap.set(key, subscriptionTypePermission);
    }

    for (var [key, value] of Object.entries(formValue.premium)) {
      var numberValue;

      if (typeof value == "boolean") {
        numberValue = Number(value);
      }
      else {
        numberValue = value;
      }

      var subscriptionTypePermission = this.premiumPermissionMap.get(key);
      subscriptionTypePermission.value = numberValue;

      this.premiumPermissionMap.set(key, subscriptionTypePermission);
    }

    var basicTypePermissions = Array.from(this.basicPermissionMap.values());
    var standardTypePermissions = Array.from(this.standardPermissionMap.values());
    var premiumTypePermissions = Array.from(this.premiumPermissionMap.values());

    const body: UpdateSubscriptionTypePermissions = {
      basic: basicTypePermissions,
      standard: standardTypePermissions,
      premium: premiumTypePermissions
    }

    this.subscriptionsService.updateAllSubscriptionTypePermissions(body).subscribe(result => {
      this.toastService.success('Änderungen wurden erfolgreich gepeichert.');
      this.loadPermissions();
    }, error => {
      this.toastService.error(error);
    })
  }
}
